import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { roleFromKnownQaEmail } from "@/lib/qaAccounts";

type HouseholdRole = "parent" | "child" | null;

async function resolveHouseholdRole(userId: string): Promise<HouseholdRole> {
  try {
    const { data, error } = await supabase.rpc("current_household_role");
    if (!error) {
      const value = (data as string) || null;
      if (value === "parent" || value === "child") return value;
    } else {
      console.warn("[useHouseholdRole] RPC failed, falling back to direct role checks:", error);
    }
  } catch (error) {
    console.warn("[useHouseholdRole] RPC threw, falling back to direct role checks:", error);
  }

  try {
    const { data: owned, error: ownedError } = await supabase
      .from("households")
      .select("id")
      .eq("owner_id", userId)
      .limit(1)
      .maybeSingle();

    if (!ownedError && owned) return "parent";
    if (ownedError) console.warn("[useHouseholdRole] household owner fallback failed:", ownedError);
  } catch (error) {
    console.warn("[useHouseholdRole] household owner fallback threw:", error);
  }

  try {
    const { data: membership, error: memberError } = await supabase
      .from("household_members")
      .select("role")
      .eq("user_id", userId)
      .limit(1)
      .maybeSingle();

    if (!memberError) {
      const value = (membership)?.role;
      if (value === "parent" || value === "child") return value;
    } else {
      console.warn("[useHouseholdRole] membership fallback failed:", memberError);
    }
  } catch (error) {
    console.warn("[useHouseholdRole] membership fallback threw:", error);
  }

  return null;
}

/**
 * Authoritative household-role check using the server-side
 * `current_household_role` RPC (SECURITY DEFINER).
 *
 * Returns 'parent' | 'child' | null (no household, or not yet loaded).
 * Used by RoleGuard to enforce billing/admin/parent-dashboard restrictions
 * on child accounts.
 */
export function useHouseholdRole() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [role, setRole] = useState<HouseholdRole>(null);
  const [loading, setLoading] = useState(true);
  const [resolvedUserId, setResolvedUserId] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    let active = true;
    const check = async () => {
      if (!user) {
        if (active) { setRole(null); setResolvedUserId(null); setLoading(false); }
        return;
      }

      // Launch-lock safety for the two official QA accounts. This prevents a
      // preview environments from briefly or permanently classifying the parent QA
      // account as a learner if role RPC/table checks are delayed, stale, or
      // pointed at a backend that has not yet finished seeding.
      const qaRole = roleFromKnownQaEmail(user.email);
      if (qaRole) {
        if (active) { setRole(qaRole); setResolvedUserId(user.id); setLoading(false); }
        return;
      }

      setLoading(true);
      const resolvedRole = await resolveHouseholdRole(user.id);
      if (!active) return;
      setRole(resolvedRole);
      setResolvedUserId(user.id);
      setLoading(false);
    };
    void check();
    return () => { active = false; };
  }, [user]);

  // Prevent one-render stale role reads during sign-in/account switches. Without
  // this, Login can see `user` from the new session while `role` is still the
  // previous null value and briefly redirect a parent to `/dashboard`.
  const effectiveLoading = loading || userId !== resolvedUserId;

  return {
    role,
    loading: effectiveLoading,
    isChild: role === "child",
    isParent: role === "parent",
  };
}
